import React, { useState, useEffect } from 'react'
import {MapPin, Bell, Smartphone, Satellite, Navigation2, Clock} from 'lucide-react'

export default function Tracker() {
  const [santaLocation, setSantaLocation] = useState({ city: 'North Pole', country: 'Arctic', giftsDelivered: 0 })

  useEffect(() => {
    // Simulate Santa's journey
    const locations = [
      { city: 'North Pole', country: 'Arctic', giftsDelivered: 0 },
      { city: 'Tokyo', country: 'Japan', giftsDelivered: 12543 },
      { city: 'Sydney', country: 'Australia', giftsDelivered: 28901 },
      { city: 'Mumbai', country: 'India', giftsDelivered: 45678 },
    ]
    
    let index = 0
    const interval = setInterval(() => {
      index = (index + 1) % locations.length
      setSantaLocation(locations[index])
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 to-blue-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?w=1920')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-4">
            <span className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold border border-green-500/30 animate-pulse">
              🎄 LIVE NOW
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Live Santa Claus<br />
            <span className="text-red-500">Tracker 2025 & 2026</span>
          </h1>
          <p className="text-xl sm:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">
            Track Father Christmas in real-time with the most advanced Santa Sleigh tracking system. Get instant SMS updates: "Where is Santa right now?"
          </p>
        </div>
      </section>

      {/* Live Tracker Display */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-3xl p-8 shadow-2xl border-4 border-red-500">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Map Visual */}
              <div className="relative bg-blue-800/30 rounded-2xl p-6 border-2 border-blue-400/30 h-80 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-red-500/10 rounded-2xl"></div>
                <div className="relative text-center">
                  <Navigation2 className="w-24 h-24 text-red-500 mx-auto mb-4 animate-bounce" />
                  <div className="text-white text-2xl font-bold mb-2">Santa is currently at:</div>
                  <div className="text-red-400 text-4xl font-bold mb-2">{santaLocation.city}</div>
                  <div className="text-white/70 text-xl">{santaLocation.country}</div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-lg">Current Location</span>
                    <MapPin className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="text-white text-3xl font-bold">{santaLocation.city}, {santaLocation.country}</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-lg">Gifts Delivered</span>
                    <Satellite className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="text-white text-3xl font-bold">{santaLocation.giftsDelivered.toLocaleString()}</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-lg">Next Stop</span>
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="text-white text-2xl font-bold">Calculating route...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SMS Updates Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-blue-950 mb-4">
              Get Live SMS Updates
            </h2>
            <p className="text-xl text-gray-600">
              Never miss Santa's location with instant text notifications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* NORAD Santa Tracker */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-200 hover:border-red-500 transition-all">
              <div className="bg-blue-900 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Satellite className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-950 mb-4">NORAD Santa Tracker Integration</h3>
              <p className="text-gray-700 mb-6">
                Get SMS updates powered by NORAD Santa Tracker, the official military tracking system used since 1955. Receive Father Christmas Tracker updates via text message.
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time Santa Sleigh position alerts</span>
                </li>
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Countdown to Santa's arrival in your timezone</span>
                </li>
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Gift delivery status updates</span>
                </li>
              </ul>
            </div>

            {/* Google Santa Tracker */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200 hover:border-blue-500 transition-all">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-950 mb-4">Google Santa Tracker Alerts</h3>
              <p className="text-gray-700 mb-6">
                Get Google Santa Tracker updates via text! Ask "Where is Santa right now?" and receive instant answers with fun facts about each location Santa visits.
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Interactive SMS games while tracking</span>
                </li>
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Fun facts about Santa's journey</span>
                </li>
                <li className="flex items-start">
                  <Bell className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Photo updates from the sleigh</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <button className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-full text-lg font-bold transition-all transform hover:scale-105 shadow-lg">
              Sign Up for SMS Tracker Updates
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-blue-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              How Santa Tracker SMS Works
            </h2>
            <p className="text-xl text-white/80">
              Three simple steps to track Father Christmas in 2025 & 2026
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                1
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Subscribe</h3>
              <p className="text-white/70">
                Enter your phone number to receive Santa Tracker 2025 alerts. It's free and takes 30 seconds!
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                2
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Track</h3>
              <p className="text-white/70">
                Get real-time texts: "Where is Santa Now?" with exact location and estimated arrival time.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                3
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Celebrate</h3>
              <p className="text-white/70">
                Get notified when Santa is in your neighborhood with countdown updates and special messages!
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
